import type { JSX } from "react";
import { Suspense } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { FormSection } from "@impero-com/common/components/formSection/formSection";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { Button, FormLabel, PageSpinner } from "@impero-com/ui";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";

import { OrgApplicationBar } from "components/applicationBar/orgApplicationBar";
import {
    accessRightSchema,
    RHFAccessRights,
} from "components/form/rhfFields/rhfAccessRights";
import { StickyFooter } from "components/stickyFooter/footer";
import { FormFooterWrapper } from "components/stickyFooter/FormFooterWrapper";
import { WebRoute } from "routes";
import {
    formatToAdminEditViewAccessRights,
    formatToUserIdAndRight,
} from "utils/accessRights";
import { ClassificationAccessRightsInstance } from "utils/accessRights/models";

import {
    useClassificationAccessRightsQuery,
    useUpdateClassificationAccessRightsMutation,
} from "../queries";

const accessRightsFormSchema = z.object({
    userIdAndRights: accessRightSchema(ClassificationAccessRightsInstance),
});
type AccessRightsFormSchema = z.infer<typeof accessRightsFormSchema>;

export function ClassificationAccessRightsPage(): JSX.Element {
    const { t } = useTranslation();
    const { classificationId } = useParams();

    if (!classificationId) {
        return <LoggedInHTTPError code={404} />;
    }

    return (
        <OrgApplicationBar
            title={{
                title: t("Classifications_Edit_Title"),
                breadCrumbs: [
                    {
                        label: t(
                            "Report_BreadCrumb_Classification_Classifications"
                        ),
                        url: WebRoute.REPORTS_CLASSIFICATION_CLASSIFICATIONS,
                    },
                ],
            }}
        >
            <Suspense fallback={<PageSpinner />}>
                <View
                    key={classificationId}
                    classificationId={classificationId}
                />
            </Suspense>
        </OrgApplicationBar>
    );
}

interface ViewProps {
    classificationId: string;
}

function View({ classificationId }: ViewProps): JSX.Element {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data } = useClassificationAccessRightsQuery(classificationId);
    const { mutateAsync, isPending } =
        useUpdateClassificationAccessRightsMutation();

    const { control, formState, handleSubmit } =
        useForm<AccessRightsFormSchema>({
            resolver: zodResolver(accessRightsFormSchema),
            mode: "onBlur",
            defaultValues: {
                userIdAndRights: formatToUserIdAndRight(data.accessRights),
            },
        });

    const handleSave = handleSubmit(async ({ userIdAndRights }) => {
        const accessRights = formatToAdminEditViewAccessRights(userIdAndRights);
        await mutateAsync({
            classificationId,
            data: {
                Id: classificationId,
                UserIdsWithAdminAccess: accessRights.admin,
                UserIdsWithEditAccess: accessRights.edit,
                UserIdsWithViewAccess: accessRights.view,
            },
        });
        navigate(WebRoute.REPORTS_CLASSIFICATION_CLASSIFICATIONS);
    });

    return (
        <FormFooterWrapper>
            <FormSection
                id="classification-access-rights"
                titleLabel={t("Classifications_Edit_Title")}
            >
                <FormLabel>{t("Classifications_Edit_Assign_Users")}</FormLabel>
                <RHFAccessRights
                    control={control}
                    users={data.users}
                    name="userIdAndRights"
                    resourceAccessRights={ClassificationAccessRightsInstance}
                />
            </FormSection>
            <StickyFooter>
                <Button
                    size="large"
                    variant="primary"
                    disabled={!formState.isDirty}
                    loading={isPending}
                    onClick={() => void handleSave()}
                >
                    {t("Classifications_EditAccessRights_Save")}
                </Button>
                <Button
                    size="large"
                    variant="default"
                    onClick={() =>
                        navigate(
                            WebRoute.REPORTS_CLASSIFICATION_CLASSIFICATIONS
                        )
                    }
                >
                    {t("Cancel")}
                </Button>
            </StickyFooter>
        </FormFooterWrapper>
    );
}
